import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import { symbolData, BLANK_SYMBOL_ID } from "../../SymbolData";
import { Settings } from "../../settings";
import { formatToCustomLocale } from "../../utilities";
import { createCoinLabel, setCoinLabel } from "./coinLabel";
import { COIN_LABEL_MAX_WIDTH_RATIO } from "../Shared/coinValueLabel";

/**
 * HoldAndWinOverlayCell — the live spine of one Hold & Win cell.
 *
 * When a cell lands on anything but a blank, the board hides the cell's
 * static picture and this plays the symbol's spine in its place: `in`, then
 * its idle, looping. The value rides INSIDE the spine, on its text slot, so it
 * moves with the landing.
 *
 *   coin (c)          in -> win -> idle   value on txt_slot_coin
 *   jackpot coin (cj) in -> win -> idle   NO value — the tier word is its face
 *   chest (ch skins)  in -> closed_idle   value on txt_slot_chest
 *                     (collected: open -> open_idle — see collect())
 *                     (End Collector: close -> closed_idle — see darken())
 *   scarab (scr)      in -> idle
 *                     (settlement: win + its "×N" popping in — revealScarab())
 *   jackpot coin hit by a scarab: double_value — the tier word shrinks away and
 *                     the multiplied number pops in, in the tier's colours
 *                     (revealJackpotValue())
 *
 * One Spine per symbol rig per cell, built the first time that rig lands here
 * and reused after — never rebuilt per spin.
 */

const IDLE_BY_RIG = { ch: "closed_idle" };
// The coin rigs — a landing plays `win` between `in` and the idle.
const COIN_RIGS = new Set(["c", "cj"]);
// `cj` prints nothing: the jackpot coin's face is its tier word (MINI …
// GRAND), and the value showed through behind it (Vladimir, 2026-09-29).
// Its `double_value` clip — the moment it turns into a value — is a later step.
// const TEXT_SLOT_BY_RIG = { c: "txt_slot_coin", cj: "txt_slot_coin", ch: "txt_slot_chest" };
const TEXT_SLOT_BY_RIG = { c: "txt_slot_coin", ch: "txt_slot_chest" };

// A jackpot coin's revealed number takes its tier word's colours (fontStyles
// coin_value_<tier>.json), keyed by the cj skin it landed with.
const JACKPOT_STYLE_BY_SKIN = {
  Jp_mini: "coinValueMini",
  Jp_minor: "coinValueMinor",
  Jp_midi: "coinValueMidi",
  Jp_major: "coinValueMajor",
  Jp_grand: "coinValueGrand",
};

// The scarab's "×N": the coin number's face, larger (a stand-in until the
// animator delivers the reveal clip with its own number — animator TODO).
const SCARAB_LABEL_SCALE = 1.4;
// Its pop-in, ms: grows past full size, then settles.
const SCARAB_POP_MS = 320;
const SCARAB_POP_OVERSHOOT = 1.25;
// The scarab's cell sorts above every other cell (see play()).
const SCARAB_Z_INDEX = 100;

export default class HoldAndWinOverlayCell {
  constructor(container) {
    this.container = container;
    this.cellHeight = 0;
    this.rigs = new Map(); // rig name -> { spine, text }
    this.active = null;
    // Resolves when the current landing has played out (see landed()).
    this._landed = Promise.resolve();
    this._settleLanded = null;
    // The skin the current rig landed with (a jackpot coin's tier).
    this._skin = null;
    // The scarab's "×N" (revealScarab), and its pop tween.
    this._multLabel = null;
    this._popTick = null;
  }

  /** The rig on this cell now — "c", "cj", "ch", "scr" — or null. */
  rig() {
    for (const [name, entry] of this.rigs) if (entry === this.active) return name;
    return null;
  }

  /**
   * Play the rig's `win` once, then back to its idle. Resolves when `win`
   * completes (or is cut). Resolved at once for a rig without one (the chest).
   */
  playWin() {
    const spine = this.active?.spine;
    const data = spine?.skeleton?.data;
    if (!data?.findAnimation("win")) return Promise.resolve();
    this._endLanding();
    const rigName = this.rig();
    const idle = IDLE_BY_RIG[rigName] ?? "idle";
    spine.state.clearTracks();
    const winEntry = spine.state.setAnimation(0, "win", false);
    if (data.findAnimation(idle)) spine.state.addAnimation(0, idle, true, 0);
    return new Promise((resolve) => {
      winEntry.listener = { complete: () => resolve(), end: () => resolve() };
    });
  }

  /**
   * A scarab reveals (GDD "Scarab"): its `win`, with its multiplier popping in
   * over it as "×N" — and staying there. Resolves when `win` completes.
   */
  revealScarab(multiplier) {
    const spine = this.active?.spine;
    if (!spine || this.rig() !== "scr") return Promise.resolve();
    if (!this._multLabel) this._multLabel = createCoinLabel();
    const label = this._multLabel;
    if (label.parent !== spine) spine.addChild(label);
    setCoinLabel(
      label,
      `×${multiplier}`,
      Settings.SYMBOL_HEIGHT *
        Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER *
        SCARAB_LABEL_SCALE,
      Settings.SYMBOL_HEIGHT * 0.9,
    );
    this._pop(label);
    return this.playWin();
  }

  /** The scarab's "×N" shown at once, no pop and no `win` (a skip). */
  showMultiplier(multiplier) {
    const spine = this.active?.spine;
    if (!spine || this.rig() !== "scr") return;
    const text = `×${multiplier}`;
    if (this._multLabel?._label === text) return;
    if (!this._multLabel) this._multLabel = createCoinLabel();
    if (this._multLabel.parent !== spine) spine.addChild(this._multLabel);
    setCoinLabel(
      this._multLabel,
      text,
      Settings.SYMBOL_HEIGHT *
        Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER *
        SCARAB_LABEL_SCALE,
      Settings.SYMBOL_HEIGHT * 0.9,
    );
  }

  /**
   * A jackpot coin a scarab has multiplied: `double_value` — its tier word
   * shrinks away and `value` pops in on txt_slot_coin, in the tier's colours.
   * Resolves when the clip completes. No-op on anything but a jackpot coin.
   */
  revealJackpotValue(value) {
    const entry = this.active;
    if (!entry || entry !== this.rigs.get("cj")) return Promise.resolve();
    const { spine } = entry;
    if (!entry.text && spine.skeleton.findSlot("txt_slot_coin")) {
      entry.text = createCoinLabel();
      spine.addSlotObject("txt_slot_coin", entry.text);
    }
    entry.styleName = JACKPOT_STYLE_BY_SKIN[this._skin] ?? "coinValue";
    this._setValue(entry, value);
    const data = spine.skeleton.data;
    if (!data.findAnimation("double_value")) return Promise.resolve();
    this._endLanding();
    spine.state.clearTracks();
    const revealEntry = spine.state.setAnimation(0, "double_value", false);
    // Then its `win` once, like every other coin a scarab hits — the player
    // sees that something happened to it (Vladimir, 2026-09-29).
    let lastEntry = revealEntry;
    if (data.findAnimation("win")) {
      lastEntry = spine.state.addAnimation(0, "win", false, 0);
    }
    if (data.findAnimation("idle")) spine.state.addAnimation(0, "idle", true, 0);
    // ── The tier word goes for GOOD ──
    // double_value shrinks it (bone coin_jckpt -> scale 0), but the clips after
    // it do not key that bone, so it springs back to full size the moment
    // double_value ends — word and number on the coin at once (checked frame
    // by frame with spine-core). So once it has shrunk, its attachment comes
    // off: no clip keys coin_jackpot's attachment, and play() puts the skin
    // back on the next landing.
    const dropWord = () => spine.skeleton.findSlot("coin_jackpot")?.setAttachment(null);
    return new Promise((resolve) => {
      if (lastEntry === revealEntry) {
        revealEntry.listener = {
          complete: () => {
            dropWord();
            resolve();
          },
          end: () => resolve(),
        };
      } else {
        revealEntry.listener = { complete: dropWord };
        lastEntry.listener = { complete: () => resolve(), end: () => resolve() };
      }
    });
  }

  /** Grow `sprite` in past full size and settle it, on the ticker. */
  _pop(sprite) {
    if (this._popTick) PIXI.Ticker.shared.remove(this._popTick);
    const base = sprite.scale.x || 1;
    let elapsed = 0;
    const tick = () => {
      elapsed += PIXI.Ticker.shared.deltaMS;
      const t = Math.min(1, elapsed / SCARAB_POP_MS);
      // 0 -> overshoot over the first 60%, overshoot -> 1 after.
      const k =
        t < 0.6
          ? SCARAB_POP_OVERSHOOT * (1 - Math.pow(1 - t / 0.6, 2))
          : SCARAB_POP_OVERSHOOT + (1 - SCARAB_POP_OVERSHOOT) * ((t - 0.6) / 0.4);
      sprite.scale.set(base * k, base * k);
      if (t >= 1) {
        PIXI.Ticker.shared.remove(tick);
        if (this._popTick === tick) this._popTick = null;
      }
    };
    this._popTick = tick;
    sprite.scale.set(0);
    PIXI.Ticker.shared.add(tick);
  }

  _hideMultiplier() {
    if (this._popTick) PIXI.Ticker.shared.remove(this._popTick);
    this._popTick = null;
    if (this._multLabel) setCoinLabel(this._multLabel, "", 0);
  }

  /**
   * Resolves when this cell's landing is OVER — its `in` (and a coin's `win`)
   * has played and the idle has started. Already resolved for a cell that is
   * not landing. The chests' collection waits on this: a chest collecting
   * before its own `in` finished was barely seen landing (Vladimir,
   * 2026-09-29: "let the land animation finish properly then start").
   */
  landed() {
    return this._landed;
  }

  _endLanding() {
    const settle = this._settleLanded;
    this._settleLanded = null;
    settle?.();
  }

  setCellHeight(height) {
    this.cellHeight = height;
    for (const [rig, entry] of this.rigs) this._scale(entry.spine, rig);
  }

  /** Play `symbolId` landing here, printing `value` if the rig has a slot. */
  play(symbolId, value = null, skinOverride = null) {
    const info = symbolData[symbolId];
    const rigName = info?.spine;
    // A blank is an empty cell — no spine, ever (its `bl` rig is the old
    // Mad Labs panel and must not be drawn).
    if (!rigName || symbolId === BLANK_SYMBOL_ID) {
      this.clear();
      return false;
    }

    const entry = this._rig(rigName);
    if (!entry) return false;

    if (this.active && this.active !== entry) this._hide(this.active);
    this.active = entry;
    // The scarab draws above every other symbol — a coin's landing next to it
    // must never cover it (Vladimir, 2026-09-30). Below the lock bars (500)
    // and the trails (1000) of HoldAndWinOverlay.
    this.container.zIndex = rigName === "scr" ? SCARAB_Z_INDEX : 0;

    const { spine } = entry;
    this._currentSymbolPay = info.symbolPay;
    this._scale(spine, rigName);
    const skin = skinOverride || info.skin;
    if (skin && spine.skeleton.data.findSkin(skin)) {
      spine.skeleton.setSkinByName(skin);
      spine.skeleton.setSlotsToSetupPose();
    }
    this._skin = skin || null;
    this._hideMultiplier();
    // this._setValue(entry, value);
    // A jackpot coin lands on its tier word — never a number, even when a
    // previous visit's reveal left a label on its slot.
    if (rigName === "cj") entry.styleName = null;
    this._setValue(entry, rigName === "cj" ? 0 : value);

    // A chest that already holds a value (collected earlier, or restored)
    // rests OPEN; an empty one rests closed.
    const idle =
      rigName === "ch" && value > 0
        ? "open_idle"
        : IDLE_BY_RIG[rigName] ?? "idle";
    const data = spine.skeleton.data;
    // A landing still owed from before is over now — this one replaces it.
    this._endLanding();
    spine.state.clearTracks();
    if (data.findAnimation("in")) {
      const inEntry = spine.state.setAnimation(0, "in", false);
      let lastEntry = inEntry;
      // A COIN lands with a flourish: in -> win -> idle (Vladimir,
      // 2026-09-29). Chests and scarabs go straight to their idle.
      if (COIN_RIGS.has(rigName) && data.findAnimation("win")) {
        lastEntry = spine.state.addAnimation(0, "win", false, 0);
      }
      let idleEntry = null;
      if (data.findAnimation(idle)) {
        idleEntry = spine.state.addAnimation(0, idle, true, 0);
      }
      // The landing is over when the idle STARTS — or, with no idle, when the
      // last landing clip completes. The spine's own event, never a timer.
      this._landed = new Promise((resolve) => {
        this._settleLanded = resolve;
      });
      const end = () => this._endLanding();
      if (idleEntry) idleEntry.listener = { start: end };
      else lastEntry.listener = { complete: end };
    } else if (data.findAnimation(idle)) {
      spine.state.setAnimation(0, idle, true);
    }
    // First frame now, so the swap from the static picture cannot flash.
    spine.state.update(0);
    spine.state.apply(spine.skeleton);
    spine.visible = true;
    spine.autoUpdate = true;
    return true;
  }

  /**
   * A chest collects (GDD "Chest" / "Golden Chest"): `open`, then `open_idle`,
   * and its value becomes the server's — ONCE. A chest already open (a golden
   * chest recollecting) only takes its new number and keeps playing
   * `open_idle`. `instant` (a skip) goes straight to `open_idle` with the value.
   * No-op on anything but a chest.
   *
   * Returns a promise that resolves when `open` has PLAYED OUT (the moment
   * `open_idle` starts) — the collection fires its trails only then, into a
   * chest that is already open (Vladimir, 2026-09-29: "use animations proper
   * and real timings and events"). Resolved at once for a skip or a non-chest.
   */
  collect(value, instant = false) {
    this._endLanding();
    const entry = this.active;
    if (!entry || entry !== this.rigs.get("ch")) return Promise.resolve();
    const { spine } = entry;
    this._setValue(entry, value);
    // ── It opens ONCE ──
    // A chest that is already open (a golden chest recollecting, or any chest
    // collecting again) stays on what it is playing — only its number changes.
    // open -> open_idle happens a single time, and nothing more until the End
    // Collector shuts it (Vladimir, 2026-09-29).
    const playing = spine.state.getCurrent(0)?.animation?.name;
    if (playing === "open" || playing === "open_idle") return Promise.resolve();
    const data = spine.skeleton.data;
    spine.state.clearTracks();
    let opened = Promise.resolve();
    if (!instant && data.findAnimation("open")) {
      const openEntry = spine.state.setAnimation(0, "open", false);
      let idleEntry = null;
      if (data.findAnimation("open_idle")) {
        idleEntry = spine.state.addAnimation(0, "open_idle", true, 0);
      }
      opened = new Promise((resolve) => {
        if (idleEntry) idleEntry.listener = { start: () => resolve() };
        else openEntry.listener = { complete: () => resolve() };
      });
    } else if (data.findAnimation("open_idle")) {
      spine.state.setAnimation(0, "open_idle", true);
    }
    spine.state.update(0);
    spine.state.apply(spine.skeleton);
    return opened;
  }

  /**
   * Resolves when the idle the cell is looping now reaches the end of its
   * current pass (`open_idle` on an open chest). The pause after a chest has
   * collected is this, not a number. Resolved at once with nothing looping.
   */
  idleLoopEnd() {
    const current = this.active?.spine?.state?.getCurrent?.(0);
    if (!current || !current.loop) return Promise.resolve();
    return new Promise((resolve) => {
      const previous = current.listener;
      current.listener = {
        ...previous,
        complete: (e) => {
          previous?.complete?.(e);
          current.listener = previous;
          resolve();
        },
      };
    });
  }

  /**
   * Collected by the End Collector: the rig's `darken` clip (the coin has it —
   * it dims the value), held on its last frame (GDD: "dim its value to
   * indicate completion"). The chest has none and closes instead (below).
   */
  darken() {
    const spine = this.active?.spine;
    // ── The chest has no `darken`: it SHUTS ──
    //
    // Its "collected" is the lid coming down — `close`, then `closed_idle`
    // looping — with its value already off the slot (the End Collector sets it
    // to 0 first). Vladimir, 2026-09-29: it "needs something to tell us that we
    // have collected that value".
    if (spine && this.active === this.rigs.get("ch")) {
      const data = spine.skeleton.data;
      if (!data.findAnimation("close")) return;
      spine.state.clearTracks();
      spine.state.setAnimation(0, "close", false);
      if (data.findAnimation("closed_idle")) {
        spine.state.addAnimation(0, "closed_idle", true, 0);
      }
      return;
    }
    if (!spine?.skeleton.data.findAnimation("darken")) return;
    spine.state.clearTracks();
    spine.state.setAnimation(0, "darken", false);
  }

  /** The landed spine's value sprite (its on-screen size is read off it). */
  valueLabel() {
    return this.active?.text ?? null;
  }

  /** Rewrite the value on the landed spine's text slot (a count-up). */
  setValue(value) {
    if (this.active) this._setValue(this.active, value);
  }

  /** Take the spine off this cell (the next spin, or a blank). */
  clear() {
    this._endLanding();
    this._hideMultiplier();
    if (this.active) this._hide(this.active);
    this.active = null;
    this.container.zIndex = 0;
  }

  destroy() {
    this._endLanding();
    this._hideMultiplier();
    for (const { spine } of this.rigs.values()) spine.destroy({ children: true });
    this.rigs.clear();
    this.active = null;
  }

  // ── internals ──

  _rig(rigName) {
    if (this.rigs.has(rigName)) return this.rigs.get(rigName);
    const skeleton = `${rigName}-skeleton`;
    const atlas = `${rigName}-atlas`;
    if (!PIXI.Assets.cache.has(skeleton) || !PIXI.Assets.cache.has(atlas)) {
      return null;
    }
    const spine = Spine.from({ skeleton, atlas });
    spine.visible = false;
    spine.autoUpdate = false;
    this.container.addChild(spine);

    let text = null;
    const slot = TEXT_SLOT_BY_RIG[rigName];
    if (slot && spine.skeleton.findSlot(slot)) {
      // A Sprite from the label cache (coinLabel.js), at the rig's own size:
      // the spine is scaled to the cell and the label inherits that scale
      // inside the slot. Never rounded — it rides the landing.
      text = createCoinLabel();
      spine.addSlotObject(slot, text);
    }

    const entry = { spine, text };
    this.rigs.set(rigName, entry);
    return entry;
  }

  _scale(spine, rigName) {
    if (!this.cellHeight) return;
    // The same size as the static picture (HoldAndWinCell.artScale).
    const pay = this.active?.spine === spine ? this._currentSymbolPay : null;
    const category =
      (pay && Settings.SYMBOL_SCALE_OVERRIDES?.[pay]) ??
      Settings.SYMBOL_SCALE_OVERRIDES?.DEFAULT ??
      1;
    spine.scale.set((this.cellHeight / Settings.SYMBOL_HEIGHT) * category);
  }

  _setValue(entry, value) {
    const { text } = entry;
    if (!text) return;
    setCoinLabel(
      text,
      value > 0 ? formatToCustomLocale(value) : "",
      Settings.SYMBOL_HEIGHT * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER,
      // Settings.SYMBOL_HEIGHT * 0.9,
      Settings.SYMBOL_HEIGHT * COIN_LABEL_MAX_WIDTH_RATIO,
      // A revealed jackpot coin keeps its tier's colours (revealJackpotValue).
      entry.styleName || "coinValue",
    );
  }

  _hide(entry) {
    entry.spine.state.clearTracks();
    entry.spine.visible = false;
    entry.spine.autoUpdate = false;
  }
}
